import React, { useEffect, useRef, useState, useMemo } from 'react';
import { SelectPicker } from 'rsuite';
import 'rsuite/dist/rsuite.min.css';
import * as echarts from 'echarts';
import SectionTitle from "./SectionTitle";
import '../assets/css/sigmap.css';
import '../assets/css/map.css';

const MapItem = () => {
    const chartRef = useRef(null);
    const containerRef = useRef(null);

    const [indicator, setIndicator] = useState('trade');
    const [year, setYear] = useState('2022');
    const [mapReady, setMapReady] = useState(false);

    // ----------------------------
    // STATIC GEO COORDS
    // ----------------------------
    const geoCoordMap = useMemo(() => ({
        Nigeria: [8.67, 9.06],
        Ghana: [-0.20, 7.97],
        'Ivory Coast': [-5.55, 7.54],
        Senegal: [-17.47, 14.73],
        Mali: [-8.00, 12.65],
        'Burkina Faso': [-1.52, 12.37],
        Guinea: [-13.71, 9.95],
        Benin: [2.61, 6.48],
        Niger: [2.11, 13.51],
        Togo: [1.22, 6.14],
        'Sierra Leone': [-13.29, 8.48],
        Liberia: [-10.80, 6.33],
        Gambia: [-16.65, 13.45],
        'Guinea-Bissau': [-15.60, 11.86],
        'Cape Verde': [-23.62, 15.08],
    }), []);

    const countries = useMemo(() => Object.keys(geoCoordMap), [geoCoordMap]);

    // ----------------------------
    // COLOR SYSTEM
    // ----------------------------
    const getColor = (country) => {
        const palette = {
            Nigeria: '#e74c3c',
            Ghana: '#2ecc71',
            'Ivory Coast': '#3498db',
            Senegal: '#f39c12',
            Mali: '#9b59b6',
            'Burkina Faso': '#1abc9c',
            Guinea: '#e67e22',
            Benin: '#34495e',
            Niger: '#d35400',
            Togo: '#27ae60',
            'Sierra Leone': '#8e44ad',
            Liberia: '#c0392b',
            Gambia: '#16a085',
            'Guinea-Bissau': '#2980b9',
            'Cape Verde': '#7f8c8d'
        };
        return palette[country] || '#95a5a6';
    };

    // ----------------------------
    // INIT MAP
    // ----------------------------
    useEffect(() => {
        let mounted = true;

        const init = async () => {
            try {
                const res = await fetch("https://fastly.jsdelivr.net/npm/echarts@4.9.0/map/json/world.json");
                const world = await res.json();

                const cedeaoGeoJSON = {
                    ...world,
                    features: world.features.filter(f =>
                        countries.includes(f.properties.name)
                    )
                };

                echarts.registerMap('cedeao', cedeaoGeoJSON);

                if (!mounted) return;

                const chart = echarts.init(containerRef.current);
                chartRef.current = chart;

                chart.setOption({
                    tooltip: { trigger: 'item' },
                    geo: {
                        map: 'cedeao',
                        roam: true,
                        layoutCenter: ['50%', '50%'],
                        layoutSize: '100%',
                        zoom: 1.1,
                        label: { show: true, fontSize: 10 },
                        itemStyle: {
                            areaColor: '#f5f7fa',
                            borderColor: '#ccc'
                        },
                        emphasis: {
                            itemStyle: { areaColor: '#5470c6' }
                        }
                    },
                    series: []
                });

                window.addEventListener('resize', () => chart.resize());

                setMapReady(true);
            } catch (err) {
                console.error(err);
            }
        };

        init();
        return () => {
            mounted = false;
            chartRef.current?.dispose();
        };
    }, [countries]);

    // ----------------------------
    // DATA UPDATE
    // ----------------------------
    useEffect(() => {
        if (!mapReady) return;

        const chart = chartRef.current;
        if (!chart) return;

        const baseFlows = [
            { from: 'Nigeria', to: 'Ghana', value: 150 },
            { from: 'Nigeria', to: 'Ivory Coast', value: 80 },
            { from: 'Senegal', to: 'Mali', value: 45 },
            { from: 'Ghana', to: 'Burkina Faso', value: 60 },
            { from: 'Ivory Coast', to: 'Mali', value: 35 },
            { from: 'Nigeria', to: 'Benin', value: 90 },
            { from: 'Senegal', to: 'Gambia', value: 25 },
        ];

        const indicatorFactor = { trade: 1, migration: 0.6, energy: 1.4 }[indicator] || 1;
        const yearFactor = { '2018': 0.8, '2020': 0.9, '2022': 1, '2024': 1.1 }[year] || 1;

        const flows = baseFlows.map(f => ({
            ...f,
            value: Math.round(f.value * indicatorFactor * yearFactor)
        }));

        // Secure lines (avoid undefined coords)
        const linesData = flows
            .filter(f => geoCoordMap[f.from] && geoCoordMap[f.to])
            .map(f => ({
                coords: [geoCoordMap[f.from], geoCoordMap[f.to]],
                fromName: f.from,
                toName: f.to,
                value: f.value,
                lineStyle: {
                    width: Math.max(f.value / 30, 1),
                    color: getColor(f.from),
                    curveness: 0.25
                }
            }));

        const scatterData = countries.map(name => {
            const exports = flows.filter(f => f.from === name).reduce((s, f) => s + f.value, 0);
            const imports = flows.filter(f => f.to === name).reduce((s, f) => s + f.value, 0);
            const total = exports + imports;

            return {
                name,
                value: [...geoCoordMap[name], total],
                exports,
                imports,
                total
            };
        });

        chart.setOption({
            series: [
                {
                    type: 'lines',
                    coordinateSystem: 'geo',
                    effect: {
                        show: true,
                        symbol: 'arrow',
                        symbolSize: 5,
                        trailLength: 0.4,
                        period: 4
                    },
                    lineStyle: { opacity: 0.8 },
                    data: linesData
                },
                {
                    type: 'scatter',
                    coordinateSystem: 'geo',
                    symbolSize: val => Math.max(Math.sqrt(val[2]) / 2, 8),
                    label: {
                        show: true,
                        position: 'right',
                        formatter: p => `${p.data.name}\n${p.data.total}`
                    },
                    itemStyle: {
                        color: p => getColor(p.data.name),
                        opacity: 0.9
                    },
                    emphasis: { scale: true },
                    data: scatterData
                }
            ]
        });

    }, [indicator, year, mapReady, geoCoordMap, countries]);

    return (
        <div className="container about-list-items mb-5">
            <SectionTitle pageTitle="map" />

            <div className="row mt-2">
                <div className="col-md-4 mt-2">
                    <SelectPicker
                        data={[
                            { label: 'Trade Flows', value: 'trade' },
                            { label: 'Migration', value: 'migration' },
                            { label: 'Energy Exchange', value: 'energy' }
                        ]}
                        value={indicator}
                        onChange={val => val && setIndicator(val)}
                        cleanable={false}
                        searchable={false}
                        style={{ width: '100%' }}
                    />
                </div>

                <div className="col-md-4 mt-2">
                    <SelectPicker
                        data={['2018','2020','2022','2024'].map(y => ({ label: y, value: y }))}
                        value={year}
                        onChange={val => val && setYear(val)}
                        cleanable={false}
                        searchable={false}
                        style={{ width: '100%' }}
                    />
                </div>
            </div>

            <div className="map-card card mt-4">
                <div className="card-body" style={{ height: 600 }}>
                    <div ref={containerRef} style={{ height: '100%' }} />
                </div>
            </div>
        </div>
    );
};

export default MapItem;